/**
 * Person record — the unified hub for birth data + email + WhatsApp + provenance.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  AD-019 (LOCKED 2026-06-19) — Person = unified hub.
 *  AD-020 (LOCKED 2026-06-19) — syncPersonToAstroEngine bi-directional sync.
 *
 *  A person record is stored in TWO places with the SAME `personId`:
 *    • AVBP:  clients/{cid}/persons/{personId}
 *    • Astro: astro/_av/people/{personId}     (mirror, via syncPersonToAstroEngine CF)
 *
 *  It carries the birth fields (so the astrology engine can chart the person),
 *  the relationship to the client, optional prakriti (Phase V), AND the contact
 *  channels (email + WhatsApp in E.164) so the astrology engine can independently
 *  email / WhatsApp any person in its database without routing through AVBP.
 *
 *  `data_source` records which surface LAST wrote the record (provenance), and
 *  `data_source_updated_at` is the server time of that write. The reverse path
 *  (astro engine direct entry) writes `data_source = 'astro_engine_direct'`.
 *
 *  personId convention (AVBP intake):
 *    • Primary client person  → `${cid}_primary`
 *    • Family / other persons → the person row's stable id (persisted in the
 *      intake doc at answers.persons[].id, stable across re-submits).
 * ─────────────────────────────────────────────────────────────────────────
 */

import type { Timestamp } from 'firebase/firestore';

/**
 * Which surface last wrote the person record (AD-019 enum). Lets the team always
 * know the provenance of a person's data.
 *   • astro_engine_direct  — entered directly in the astrology engine.
 *   • avbp_intake_client   — submitted by the client via a public intake link.
 *   • avbp_intake_staff    — filled by staff on the client's behalf in the portal.
 *   • avbp_contact_card    — created/edited from the AVBP contact card.
 */
export type PersonDataSource =
  | 'astro_engine_direct'
  | 'avbp_intake_client'
  | 'avbp_intake_staff'
  | 'avbp_contact_card';

/* ─────────────────────── M5-S6 — two-axis relationship (AD-027) ───────────────────────
 *
 * A person carries TWO INDEPENDENT relationship axes:
 *
 *   Axis 1 — participant_type — the COARSE role for Vastu zones + portal
 *     visibility + legal. Drives "who counts as an occupant/owner". This is the
 *     EXISTING S5 vocabulary (unchanged): co_owner · family_member · tenant ·
 *     external_professional (external_professional is staff-only).
 *
 *   Axis 2 — relation_to_client — the SPECIFIC familial/business relation to the
 *     PRIMARY client (the native). Drives relational astrology (spouse composite,
 *     progeny via son/daughter, parental charts, etc.). The astro engine needs the
 *     precise relation that participant_type deliberately collapses away.
 *
 * Both are OPTIONAL. The legacy single overloaded `relationship` field is kept
 * for back-compat (S5 filled it with a participant_type value; an even older
 * personModel comment filled it with a familial value). `readRelationAxes()`
 * resolves a doc of any vintage into the two axes.
 * ───────────────────────────────────────────────────────────────────────────── */

/** Axis 1 — coarse participant type (Vastu / visibility / legal). S5 vocabulary.
 *  L1A redesign (2026-06-21) adds `owner` (the primary owner, distinct from a
 *  co_owner) — a client-visible type. external_professional stays staff-only. */
export type ParticipantType =
  | 'owner'
  | 'co_owner'
  | 'family_member'
  | 'tenant'
  | 'external_professional';

/** The participant types in canonical order (external_professional is staff-only). */
export const PARTICIPANT_TYPE_VALUES: readonly ParticipantType[] = [
  'owner',
  'co_owner',
  'family_member',
  'tenant',
  'external_professional',
] as const;

/**
 * Axis 2 — specific relation to the primary client/native (relational astrology).
 * `self` = the native; `other` = the escape hatch for an unlisted relation.
 *
 * nephew / niece / cousin added (over the brief's base list) for astro fidelity —
 * they are common joint-family chart members and the engine charts them like any
 * other relative; documented in AD-027.
 */
export type RelationToClient =
  | 'self'
  | 'spouse'
  | 'son'
  | 'daughter'
  | 'son_in_law'
  | 'daughter_in_law'
  | 'father'
  | 'mother'
  | 'father_in_law'
  | 'mother_in_law'
  | 'elder_brother'
  | 'younger_brother'
  | 'elder_sister'
  | 'younger_sister'
  | 'brother_in_law'
  | 'sister_in_law'
  | 'grandfather'
  | 'grandmother'
  | 'grandson'
  | 'granddaughter'
  | 'nephew'
  | 'niece'
  | 'cousin'
  | 'business_partner'
  | 'other';

/** The relation vocabulary in canonical (UI-ordered) form. */
export const RELATION_TO_CLIENT_VALUES: readonly RelationToClient[] = [
  'self',
  'spouse',
  'son',
  'daughter',
  'son_in_law',
  'daughter_in_law',
  'father',
  'mother',
  'father_in_law',
  'mother_in_law',
  'elder_brother',
  'younger_brother',
  'elder_sister',
  'younger_sister',
  'brother_in_law',
  'sister_in_law',
  'grandfather',
  'grandmother',
  'grandson',
  'granddaughter',
  'nephew',
  'niece',
  'cousin',
  'business_partner',
  'other',
] as const;

/** UI grouping of the relation vocabulary (optgroup labels → values). */
export const RELATION_TO_CLIENT_GROUPS: { label: string; values: RelationToClient[] }[] = [
  { label: 'Self & immediate', values: ['self', 'spouse', 'son', 'daughter', 'son_in_law', 'daughter_in_law'] },
  { label: 'Parents', values: ['father', 'mother', 'father_in_law', 'mother_in_law'] },
  { label: 'Siblings', values: ['elder_brother', 'younger_brother', 'elder_sister', 'younger_sister'] },
  { label: 'In-laws (siblings)', values: ['brother_in_law', 'sister_in_law'] },
  { label: 'Extended', values: ['grandfather', 'grandmother', 'grandson', 'granddaughter', 'nephew', 'niece', 'cousin'] },
  { label: 'Business', values: ['business_partner'] },
  { label: 'Other', values: ['other'] },
];

/* ─────────────────── M5-S6b — Axis 3: work / reporting (AD-027) ───────────────────
 *
 * Axis 3 captures the ORGANIZATIONAL / reporting structure of a person on a
 * work/business property (office · commercial · institutional · retail · industrial),
 * and on a family-business residence that needs BOTH the family axis AND the org
 * axis. It is INDEPENDENT of, and OPTIONAL alongside, Axis 1 (participant_type) and
 * Axis 2 (relation_to_client). A single person can carry family AND work axes at once.
 *
 *   • work_role   — the person's designation in the organisation.
 *   • reports_to  — a reference (by the person's stable intake row id) to ANOTHER
 *                   person already added in the SAME intake. This single edge is what
 *                   captures the reporting STRUCTURE (the org-chart edge). Self- and
 *                   obvious-cycle references are guarded in the UI (a manager cannot be
 *                   the person themself; a soft cycle warning is enough — full graph
 *                   validation is part of the DEFERRED org-hierarchy compute).
 *   • department  — the functional department, when known.
 *
 * ALL THREE are OPTIONAL. The org-hierarchy COMPUTE (seating/zone analysis by
 * reporting line, head-of-org placement, departmental zoning) is DEFERRED to a
 * dedicated build stream — Axis 3 is intake-capture only here.
 * ───────────────────────────────────────────────────────────────────────────── */

/** Axis 3a — designation / work role. `other` = escape hatch. */
export type WorkRole =
  | 'founder'
  | 'co_founder'
  | 'proprietor'
  | 'partner'
  | 'chairman'
  | 'director'
  | 'ceo'
  | 'coo'
  | 'cfo'
  | 'general_manager'
  | 'manager'
  | 'team_lead'
  | 'supervisor'
  | 'employee'
  | 'accountant'
  | 'consultant'
  | 'vendor'
  | 'contractor'
  | 'other';

/** The work-role vocabulary in canonical (UI-ordered) form. */
export const WORK_ROLE_VALUES: readonly WorkRole[] = [
  'founder',
  'co_founder',
  'proprietor',
  'partner',
  'chairman',
  'director',
  'ceo',
  'coo',
  'cfo',
  'general_manager',
  'manager',
  'team_lead',
  'supervisor',
  'employee',
  'accountant',
  'consultant',
  'vendor',
  'contractor',
  'other',
] as const;

/** UI grouping of the work-role vocabulary (optgroup labels → values). */
export const WORK_ROLE_GROUPS: { label: string; values: WorkRole[] }[] = [
  { label: 'Ownership', values: ['founder', 'co_founder', 'proprietor', 'partner'] },
  { label: 'Leadership', values: ['chairman', 'director', 'ceo', 'coo', 'cfo'] },
  { label: 'Management', values: ['general_manager', 'manager', 'team_lead', 'supervisor'] },
  { label: 'Staff', values: ['employee', 'accountant'] },
  { label: 'External', values: ['consultant', 'vendor', 'contractor'] },
  { label: 'Other', values: ['other'] },
];

/** Axis 3c — functional department. `other` = escape hatch. */
export type Department =
  | 'admin'
  | 'finance'
  | 'sales'
  | 'operations'
  | 'hr'
  | 'production'
  | 'marketing'
  | 'it'
  | 'other';

/** The department vocabulary in canonical (UI-ordered) form. */
export const DEPARTMENT_VALUES: readonly Department[] = [
  'admin',
  'finance',
  'sales',
  'operations',
  'hr',
  'production',
  'marketing',
  'it',
  'other',
] as const;

/**
 * Human label for a work_role / department value (e.g. `co_founder` → `Co-founder`,
 * `ceo` → `CEO`, `it` → `IT`). Falls back to title-cased words for any unknown string.
 */
export function workAxisLabel(value: string): string {
  const special: Record<string, string> = {
    co_founder: 'Co-founder',
    ceo: 'CEO',
    coo: 'COO',
    cfo: 'CFO',
    general_manager: 'General manager',
    team_lead: 'Team lead',
    hr: 'HR',
    it: 'IT',
  };
  if (special[value]) return special[value];
  return value
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function isParticipantType(v: unknown): v is ParticipantType {
  return typeof v === 'string' && (PARTICIPANT_TYPE_VALUES as readonly string[]).includes(v);
}
function isRelationToClient(v: unknown): v is RelationToClient {
  return typeof v === 'string' && (RELATION_TO_CLIENT_VALUES as readonly string[]).includes(v);
}

/**
 * Human label for a relation_to_client value (e.g. `son_in_law` → `Son-in-law`).
 * Falls back to the raw value (title-cased words) for any unknown string.
 */
export function relationToClientLabel(value: string): string {
  const special: Record<string, string> = {
    son_in_law: 'Son-in-law',
    daughter_in_law: 'Daughter-in-law',
    father_in_law: 'Father-in-law',
    mother_in_law: 'Mother-in-law',
    brother_in_law: 'Brother-in-law',
    sister_in_law: 'Sister-in-law',
  };
  if (special[value]) return special[value];
  return value
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * BACK-COMPAT READER (AD-027) — resolve a Person doc of ANY vintage into the two
 * axes. Resolution order:
 *   participant_type ← explicit `participant_type` field, else the legacy
 *     `relationship` field IF it holds a participant-type value (S5 docs).
 *   relation_to_client ← explicit `relation_to_client` field, else the legacy
 *     `relationship` field IF it holds a familial-relation value (pre-S5 docs).
 * A legacy free-text `relationship` that is neither (e.g. "Owner") yields neither
 * axis here — it is surfaced verbatim in the UI as a one-off option, never lost.
 */
export function readRelationAxes(
  p: Partial<Person> | Record<string, unknown> | null | undefined,
): { participant_type?: ParticipantType; relation_to_client?: RelationToClient } {
  const o = (p || {}) as Record<string, unknown>;
  const out: { participant_type?: ParticipantType; relation_to_client?: RelationToClient } = {};
  if (isParticipantType(o.participant_type)) out.participant_type = o.participant_type;
  else if (isParticipantType(o.relationship)) out.participant_type = o.relationship;
  if (isRelationToClient(o.relation_to_client)) out.relation_to_client = o.relation_to_client;
  else if (isRelationToClient(o.relationship)) out.relation_to_client = o.relationship;
  return out;
}

/**
 * The unified Person record (AD-019). Stored at clients/{cid}/persons/{personId}
 * in AVBP and mirrored to astro/_av/people/{personId} by syncPersonToAstroEngine.
 *
 * Birth fields are OPTIONAL — a person captured without complete birth data is
 * still a valid record (the astrology engine simply does not chart them). Channel
 * fields (email / whatsapp) are optional; channel-send logic (AD-020) decides
 * WA / email / both / error from whichever are present.
 */
export interface Person {
  /** Display name. */
  name: string;
  /** Date of birth (ISO `YYYY-MM-DD`), when known. */
  dob?: string;
  /** Time of birth (`HH:mm`), when known. */
  tob?: string;
  /** Place of birth (human label), when known. */
  pob?: string;
  /** Birth-place latitude (finite number), when known. */
  lat?: number;
  /** Birth-place longitude (finite number), when known. */
  lon?: number;
  /** IANA timezone of the birth place (e.g. `Asia/Kolkata`) — no-DST policy. */
  tz?: string;
  /**
   * @deprecated (AD-027) — legacy single overloaded relationship field. S5 wrote
   * a participant-type value here; a pre-S5 doc may carry a familial value. New
   * writes mirror `participant_type` here for back-compat, but READ via the two
   * explicit axes below (use `readRelationAxes()`).
   */
  relationship?: string;
  /**
   * Axis 1 (AD-027) — coarse participant type: Vastu zones + portal visibility +
   * legal. co_owner · family_member · tenant · external_professional.
   */
  participant_type?: ParticipantType;
  /**
   * Axis 2 (AD-027) — specific relation to the primary client/native, for
   * relational astrology. Captured at intake; relational COMPUTE is DEFERRED.
   */
  relation_to_client?: RelationToClient;
  /**
   * Axis 3a (AD-027) — designation / work role in the organisation (offices,
   * commercial, family-business). Captured at intake; org-hierarchy COMPUTE is
   * DEFERRED.
   */
  work_role?: WorkRole;
  /**
   * Axis 3b (AD-027) — the org-chart edge: the `personId` (stable intake row id)
   * of ANOTHER person in the SAME intake that this person reports to. Optional;
   * self/cycle guarded in the UI. The reporting STRUCTURE is the set of these edges.
   */
  reports_to?: string;
  /**
   * Axis 3c (AD-027) — functional department, when known.
   */
  department?: Department;
  /** Ayurvedic prakriti classification (Phase V), when known. */
  prakriti?: string;
  /** Contact email, when known — astro engine can email independently. */
  email?: string;
  /** Contact WhatsApp number in E.164 (e.g. `+919179303585`), when known. */
  whatsapp?: string;
  /** Which surface last wrote this record (provenance). */
  data_source: PersonDataSource;
  /** Server time of the last write by `data_source`. */
  data_source_updated_at: Timestamp;
}
